/**
 * Request ID utility for end-to-end request correlation.
 *
 * Generates a UUID on first load and stores it in sessionStorage when available.
 * If browser storage is blocked, it falls back to one in-memory UUID for the current tab.
 * The same request_id is reused for the entire browser session or tab lifetime,
 * allowing correlation of all frontend activity → backend logs → traces.
 *
 * Why session-scoped (not per-request)?
 *   A single request_id for the entire browser session makes it trivial to find
 *   ALL backend logs for a user's session with one Datadog search query.
 *   Individual requests within a session are distinguished by http_route + timestamp.
 *
 * Where it's used:
 *   - src/services/api.js — sent as X-Request-Id header on every API call
 *   - src/lib/datadog.js — set as global context on RUM and browser-logs
 *
 * How to find it:
 *   - Browser DevTools → Network tab → any API request → X-Request-Id header
 *   - Browser Console → sessionStorage.getItem('request_id') when storage is available
 *   - Datadog RUM → session → Global Context → request_id
 */

const SESSION_STORAGE_KEY = 'request_id';
let inMemoryRequestId = '';

function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers that don't support crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, (c) => {
    const r = Math.trunc(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getRequestId() {
  try {
    let requestId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!requestId) {
      requestId = generateUUID();
      sessionStorage.setItem(SESSION_STORAGE_KEY, requestId);
    }
    return requestId;
  } catch {
    // sessionStorage may be unavailable (e.g. private browsing, iframe restrictions).
    // Keep a module-scoped fallback so this tab still uses one stable request_id.
    if (!inMemoryRequestId) {
      inMemoryRequestId = generateUUID();
    }
    return inMemoryRequestId;
  }
}
